/**
 * Reading a typed outline: one heading per line, its level given by `#` marks, by an
 * outline number, or by indentation.
 *
 *     # Method                 ← level 1 by marks
 *     ## Participants          ← level 2
 *     2.1 Participants         ← level 2 by its outline number
 *         Recruitment          ← indented one step deeper than the line above it
 *
 * The heading and table-of-contents tools share it, so an outline typed for one
 * pastes into the other.
 */

export type LevelSource = "marks" | "number" | "indent";

export interface OutlineLine {
  /** 1-based line number in the input, for messages. */
  line: number;
  level: number;
  /** The heading without its marks, number or indentation. */
  text: string;
  /** The outline number the writer typed ("2.1"), if any. */
  number: string | null;
  source: LevelSource;
}

const RE_LINES = /\r?\n/;
const RE_MARKS = /^(#{1,9})\s*(.*)$/;
/** "2", "2.1", "2.1.3", optionally with a closing period: one or two digits a part. */
const RE_OUTLINE_NUMBER = /^(\d{1,2}(?:\.\d{1,2})*)\.?\s+(\S.*)$/;
const RE_INDENT = /^[ \t]*/;

/** Columns of indentation, a tab counting as four. */
function indentWidth(raw: string): number {
  const lead = RE_INDENT.exec(raw)?.[0] ?? "";
  let width = 0;
  for (const char of lead) {
    width += char === "\t" ? 4 : 1;
  }
  return width;
}

/** The indentation step: the smallest non-zero indent in the outline. */
function indentStep(widths: readonly number[]): number {
  const positive = widths.filter((width) => width > 0);
  return positive.length > 0 ? Math.min(...positive) : 1;
}

interface Pending {
  line: number;
  raw: string;
  indent: number;
}

function classify(item: Pending, step: number, minIndent: number): OutlineLine {
  const trimmed = item.raw.trim();
  const marks = RE_MARKS.exec(trimmed);
  if (marks) {
    return {
      line: item.line,
      level: marks[1].length,
      text: marks[2].trim(),
      number: null,
      source: "marks",
    };
  }
  const numbered = RE_OUTLINE_NUMBER.exec(trimmed);
  if (numbered) {
    return {
      line: item.line,
      level: numbered[1].split(".").length,
      text: numbered[2].trim(),
      number: numbered[1],
      source: "number",
    };
  }
  return {
    line: item.line,
    level: 1 + Math.round((item.indent - minIndent) / step),
    text: trimmed,
    number: null,
    source: "indent",
  };
}

/** Every non-blank line of `text` as a heading with a level from 1 up. */
export function parseOutline(text: string): OutlineLine[] {
  const pending: Pending[] = [];
  for (const [index, raw] of text.split(RE_LINES).entries()) {
    if (raw.trim() !== "") {
      pending.push({ line: index + 1, raw, indent: indentWidth(raw) });
    }
  }
  const indents = pending.map((item) => item.indent);
  const minIndent = indents.length > 0 ? Math.min(...indents) : 0;
  const step = indentStep(indents.map((width) => width - minIndent));
  return pending
    .map((item) => classify(item, step, minIndent))
    .filter((line) => line.text !== "");
}
